import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Package,
  ArrowRight,
  CheckCircle,
  ChartBar,
  Truck,
  ShieldCheck,
} from "@phosphor-icons/react";

export default function LoginPage() {
  const { login, register, error, setError } = useAuth();

  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setError("");
    setMode(mode === "login" ? "register" : "login");
  };

  return (
    <div className="min-h-screen bg-slate-950 lg:grid lg:grid-cols-[1.05fr_0.95fr]">
      {/* Area informativa */}
      <section className="relative hidden overflow-hidden lg:flex lg:flex-col lg:justify-between">
        <img
          src="https://images.pexels.com/photos/4487363/pexels-photo-4487363.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600"
          alt="Magazzino aziendale"
          className="absolute inset-0 h-full w-full object-cover"
        />

        <div className="absolute inset-0 bg-slate-950/75" />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-950/80 via-slate-950/55 to-slate-950/90" />

        <div className="relative z-10 flex items-center gap-3 p-10 text-white">
          <div className="grid h-12 w-12 place-items-center rounded-2xl bg-blue-600 shadow-lg shadow-blue-950/40">
            <Package size={25} weight="fill" />
          </div>

          <div>
            <div className="text-xl font-bold tracking-tight">
              A DUEA Magazzino
            </div>
            <div className="text-sm text-slate-300">
              Gestione aziendale centralizzata
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-2xl px-10 pb-12 text-white">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-blue-300/20 bg-blue-400/10 px-4 py-2 text-sm text-blue-100 backdrop-blur">
            <ShieldCheck size={18} weight="fill" />
            Controllo semplice e sicuro
          </div>

          <h1 className="max-w-xl text-5xl font-bold leading-tight tracking-tight">
            Tutto il magazzino sotto controllo.
          </h1>

          <p className="mt-5 max-w-xl text-lg leading-relaxed text-slate-300">
            Gestisci prodotti, carichi, scarichi, giacenze e report da
            un&apos;unica piattaforma.
          </p>

          <div className="mt-10 grid max-w-xl grid-cols-3 gap-4">
            <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
              <Truck size={25} className="mb-3 text-blue-300" />
              <div className="font-semibold">Movimenti</div>
              <div className="mt-1 text-sm text-slate-300">
                Carico e scarico
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
              <ChartBar size={25} className="mb-3 text-blue-300" />
              <div className="font-semibold">Report</div>
              <div className="mt-1 text-sm text-slate-300">
                Dati aggiornati
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
              <CheckCircle size={25} className="mb-3 text-blue-300" />
              <div className="font-semibold">Giacenze</div>
              <div className="mt-1 text-sm text-slate-300">
                Sempre disponibili
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Area login */}
      <main className="flex min-h-screen items-center justify-center bg-slate-50 px-5 py-10 sm:px-8">
        <div
          className="w-full max-w-md"
          data-testid="auth-panel"
        >
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-blue-600 text-white">
              <Package size={23} weight="fill" />
            </div>

            <div>
              <div className="text-lg font-bold text-slate-900">
                A DUEA Magazzino
              </div>
              <div className="text-xs text-slate-500">
                Gestionale aziendale
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-xl shadow-slate-200/60 sm:p-9">
            <div className="mb-8">
              <div className="mb-3 text-sm font-semibold uppercase tracking-widest text-blue-600">
                Area riservata
              </div>

              <h2 className="text-3xl font-bold tracking-tight text-slate-950">
                {mode === "login"
                  ? "Bentornato"
                  : "Crea il tuo account"}
              </h2>

              <p className="mt-2 leading-relaxed text-slate-500">
                {mode === "login"
                  ? "Inserisci le tue credenziali per accedere al gestionale."
                  : "Compila i dati per creare un nuovo account."}
              </p>
            </div>

            <form
              onSubmit={submit}
              className="space-y-5"
              data-testid="auth-form"
            >
              {mode === "register" && (
                <div>
                  <Label
                    htmlFor="name"
                    className="mb-2 block text-sm font-medium text-slate-700"
                  >
                    Nome e cognome
                  </Label>

                  <Input
                    id="name"
                    data-testid="register-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Mario Rossi"
                    className="h-12 rounded-xl border-slate-300 bg-slate-50 px-4 focus:bg-white"
                  />
                </div>
              )}

              <div>
                <Label
                  htmlFor="email"
                  className="mb-2 block text-sm font-medium text-slate-700"
                >
                  Indirizzo email
                </Label>

                <Input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  data-testid="auth-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nome@azienda.it"
                  className="h-12 rounded-xl border-slate-300 bg-slate-50 px-4 focus:bg-white"
                />
              </div>

              <div>
                <Label
                  htmlFor="password"
                  className="mb-2 block text-sm font-medium text-slate-700"
                >
                  Password
                </Label>

                <Input
                  id="password"
                  type="password"
                  required
                  autoComplete={
                    mode === "login"
                      ? "current-password"
                      : "new-password"
                  }
                  data-testid="auth-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Inserisci la password"
                  className="h-12 rounded-xl border-slate-300 bg-slate-50 px-4 focus:bg-white"
                />
              </div>

              {error && (
                <div
                  className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                  data-testid="auth-error"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                data-testid="auth-submit-btn"
                className="h-12 w-full rounded-xl bg-blue-600 font-semibold text-white shadow-lg shadow-blue-200 transition hover:bg-blue-700 disabled:opacity-60"
              >
                {loading
                  ? "Accesso in corso..."
                  : mode === "login"
                    ? "Accedi al gestionale"
                    : "Crea account"}

                {!loading && (
                  <ArrowRight
                    weight="bold"
                    size={18}
                    className="ml-2"
                  />
                )}
              </Button>
            </form>

            <div className="mt-7 border-t border-slate-100 pt-6 text-center text-sm text-slate-500">
              {mode === "login"
                ? "Non hai un account?"
                : "Hai già un account?"}{" "}
              <button
                type="button"
                onClick={switchMode}
                className="font-semibold text-blue-600 hover:text-blue-700 hover:underline"
                data-testid="auth-switch-mode"
              >
                {mode === "login" ? "Registrati" : "Accedi"}
              </button>
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-slate-400">
            © {new Date().getFullYear()} A DUEA S.r.l. — Gestionale magazzino
          </p>
        </div>
      </main>
    </div>
  );
}